// Angular
import {APP_INITIALIZER, ErrorHandler, LOCALE_ID, NgModule} from '@angular/core';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {BrowserModule} from '@angular/platform-browser';
import {registerLocaleData} from '@angular/common';
import {HTTP_INTERCEPTORS, HttpClientModule} from '@angular/common/http';
import localeDe from '@angular/common/locales/de';
import localeDeExtra from '@angular/common/locales/extra/de';
import localeEn from '@angular/common/locales/en';
import localeEnExtra from '@angular/common/locales/extra/en';
// Material
import {MatButtonModule} from '@angular/material/button';
import {MatCardModule} from '@angular/material/card';
import {MatIconModule} from '@angular/material/icon';
import {MatListModule} from '@angular/material/list';
import {MatMenuModule} from '@angular/material/menu';
import {MatSidenavModule} from '@angular/material/sidenav';
import {MatToolbarModule} from '@angular/material/toolbar';
import {MatSnackBarModule} from '@angular/material/snack-bar';
// Auth
import {
  AuthInterceptor as OidcAuthInterceptor,
  AuthModule,
  OidcSecurityService,
  OpenIdConfiguration,
  StsConfigHttpLoader,
  StsConfigLoader
} from 'angular-auth-oidc-client';
// Logging
import {LoggerModule, NGXLogger, NgxLoggerLevel} from 'ngx-logger';
// Portal
import {PortalMessageBroker} from './portal/portal-message-broker';
import {PortalTokenService} from './portal/portal-token.service';
// OpenAPI
import {
  ApiModule as FakturierungApiInternalModule,
  BetriebService,
  Configuration as FakturierungConfiguration
} from './openapi/fakturierung-openapi';
// App
import {environment} from '../environments/environment';
import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {StoreDevtoolsModule} from '@ngrx/store-devtools';
import {StoreModule} from '@ngrx/store';
import {EffectsModule} from '@ngrx/effects';
import {MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipModule} from '@angular/material/tooltip';
import {mergeMap, take, tap} from 'rxjs';
import {ConfigModule} from './config/config.module';
import {ConfigService} from './config/service/config.service';
import {filter, map} from 'rxjs/operators';
import {AuthInterceptor} from './auth/auth.interceptor';
import {fromPromise} from 'rxjs/internal/observable/innerFrom';
import * as Sentry from '@sentry/angular-ivy';
import jwtDecode, {JwtPayload} from 'jwt-decode';
import {metaReducers, reducers} from './store/reducers/reducers';
import {
  MatomoConsentMode,
  MatomoInitializationMode,
  MatomoInitializerService,
  NgxMatomoTrackerModule
} from '@ngx-matomo/tracker';
import {ContextInfoService, WellKnownJustFarmingProperties} from './portal/context-info.service';
import {MatDialogModule} from '@angular/material/dialog';
import {SentryEffects} from './store/effects/sentry.effects';
import {AppNavigationModule} from './modules/pages/app-navigation/app-navigation.module';
import {StoreRouterConnectingModule} from '@ngrx/router-store';
import {BetriebsauswahlPageComponent} from './modules/pages/betriebsauswahl/betriebsauswahl.page.component';
import {
  AppSkeletonModule,
  BetriebsauswahlModule,
  CONTEXT_INFO,
  ErrorPagesModule,
  INHABER_SERVICE,
  NavigationModule,
  NGX_LOGGER,
  provideDialogs,
  provideTheme,
} from '@adnova/jf-ng-components';
import {MemoizedSelector} from '@ngrx/store';
import {KundeDialogSelectors} from './store/selectors/kunde-dialog.selectors';
import {KundeDialogActions} from './store/actions/kunde-dialog.actions';
import {DeleteKundeDialogSelectors} from './store/selectors/delete-kunde-dialog.selectors';
import {DeleteKundeDialogActions} from './store/actions/delete-kunde-dialog.actions';
import {ProduktDialogSelectors} from './store/selectors/produkt-dialog.selectors';
import {ProduktDialogActions} from './store/actions/produkt-dialog.actions';
import {DeleteProduktDialogSelectors} from './store/selectors/delete-produkt-dialog.selectors';
import {DeleteProduktDialogActions} from './store/actions/delete-produkt-dialog.actions';
import {UploadLogoDialogSelectors} from './store/selectors/upload-logo-dialog.selectors';
import {UploadLogoDialogActions} from './store/actions/upload-logo-dialog.actions';
import {BelegeEmptyStateDialogSelectors} from './store/selectors/belege-emptystate-dialog.selectors';
import {BelegeEmptyStateDialogActions} from './store/actions/belege-empty-state-dialog.actions';
import {SendenDialogSelectors} from './store/selectors/senden-dialog.selectors';
import {SendenDialogActions} from './store/actions/senden-dialog.actions';
import {VersandstatusHistoryDialogSelectors} from './store/selectors/versandstatus-history-dialog.selectors';
import {VersandstatusHistoryDialogActions} from './store/actions/versandstatus-history-dialog.actions';
import {DeleteFakturierungsbelegDialogSelectors} from './store/selectors/delete-fakturierungsbeleg-dialog.selectors';
import {DeleteFakturierungsbelegDialogActions} from './store/actions/delete-fakturierungsbeleg-dialog.actions';
import {CancelFakturierungsbelegDialogSelectors} from './store/selectors/cancel-fakturierungsbeleg-dialog.selectors';
import {CancelFakturierungsbelegDialogActions} from './store/actions/cancel-fakturierungsbeleg-dialog.actions';
import {ProduktEntitiesEffects} from './store/effects/produkt-entities.effects';
import {KundeEntitiesEffects} from './store/effects/kunde-entities.effects';
import {KundeTableEffects} from './store/effects/kunde-table.effects';
import {LandEntitiesEffects} from './store/effects/land-entities.effects';
import {KontaktEffects} from './store/effects/kontakt.effects';
import {FakturierungsbelegEntitiesEffects} from './store/effects/fakturierungsbeleg-entities.effects';
import {FakturierungsbelegFormEffects} from './store/effects/fakturierungsbeleg-form.effects';
import {ProduktTableEffects} from './store/effects/produkt-table.effects';
import {UmsatzsteuerschluesselEntitiesEffects} from './store/effects/umsatzsteuerschluessel-entities.effects';
import {MengeneinheitEntitiesEffects} from './store/effects/mengeneinheit-entities.effects';
import {FakturierungsbelegTableEffects} from './store/effects/fakturierungsbeleg-table.effects';
import {AddPositionEffects} from './store/effects/add-position.effects';
import {FakturierungsbelegContentEffects} from './store/effects/fakturierungsbeleg-content.effect';
import {EinstellungenEntitiesEffects} from './store/effects/einstellungen-entities.effects';
import {VersandstatusHistoryEffects} from './store/effects/versandstatus-history.effects';
import {BelegAktualisierungenEffects} from './store/effects/beleg-aktualisierungen.effects';
import {BelegKetteEffects} from './store/effects/beleg-kette.effects';
import {SendenDialogEffects} from './store/effects/senden-dialog.effects';
import {VersandstatusPollingEffects, VISIBILITY_STREAM, visibilityStreamFactory} from './store/effects/versandstatus-polling.effects';
import {VersandstatusHistoryCacheCleanupEffects} from './store/effects/versandstatus-history-cache-cleanup.effects';


registerLocaleData(localeDe, 'de-DE', localeDeExtra);
registerLocaleData(localeEn, 'en-US', localeEnExtra);

@NgModule({
  declarations: [
    AppComponent,
    BetriebsauswahlPageComponent,
  ],
  imports: [
    // Angular
    BrowserAnimationsModule,
    BrowserModule,
    HttpClientModule,
    // Material
    MatIconModule,
    MatListModule,
    MatSidenavModule,
    MatMenuModule,
    MatButtonModule,
    MatToolbarModule,
    MatCardModule,
    MatSnackBarModule,
    MatDialogModule,
    // JF-NG-Components
    AppNavigationModule,
    AppSkeletonModule,
    BetriebsauswahlModule,
    NavigationModule,
    ErrorPagesModule,
    // Config
    ConfigModule,
    // Logging
    LoggerModule.forRoot({
      level: NgxLoggerLevel.INFO, // default logging level
      enableSourceMaps: !environment.production,
    }),
    // Auth
    // TODO: Auth ggf. in eigenes Module auslagern
    AuthModule.forRoot({
      loader: {
        provide: StsConfigLoader,
        useFactory: (configService: ConfigService) => {
          const configObservable = configService.loaded$
            .pipe(
              take(1),
              map(config => {
                const authConfig: OpenIdConfiguration = {
                  // loaded
                  authority: config.auth.authority,
                  clientId: config.auth.clientId,
                  scope: config.auth.scope,
                  logLevel: config.auth.logLevel,
                  renewTimeBeforeTokenExpiresInSeconds: config.auth.renewTimeBeforeTokenExpiresInSeconds,
                  // predefined
                  redirectUrl: window.location.origin,
                  postLogoutRedirectUri: window.location.origin,
                  responseType: 'code',
                  silentRenew: true,
                  triggerAuthorizationResultEvent: true,
                  ignoreNonceAfterRefresh: true,
                  useRefreshToken: true,
                  // INFO: Bei anpassungen auch AuthInterceptor.matchesSecureRoots() anpassen
                  secureRoutes: [
                    config.fakturierung.apiBaseUrl,
                  ],
                };
                return authConfig;
              }),
            );
          return new StsConfigHttpLoader(configObservable);
        },
        deps: [ConfigService],
      },
    }),
    // OpenAPI
    FakturierungApiInternalModule.forRoot(() => new FakturierungConfiguration()),
    // App
    AppRoutingModule,
    StoreModule.forRoot(
      reducers,
      {
        metaReducers,
        runtimeChecks: {
          strictStateImmutability: true,
          strictActionImmutability: true,
          strictActionSerializability: true,
          strictStateSerializability: true,
        }
      }),
    EffectsModule.forRoot([
      SentryEffects,
      ProduktEntitiesEffects,
      KundeEntitiesEffects,
      KundeTableEffects,
      LandEntitiesEffects,
      KontaktEffects,
      FakturierungsbelegEntitiesEffects,
      FakturierungsbelegTableEffects,
      FakturierungsbelegFormEffects,
      ProduktTableEffects,
      AddPositionEffects,
      UmsatzsteuerschluesselEntitiesEffects,
      MengeneinheitEntitiesEffects,
      FakturierungsbelegContentEffects,
      EinstellungenEntitiesEffects,
      VersandstatusHistoryEffects,
      BelegAktualisierungenEffects,
      BelegKetteEffects,
      SendenDialogEffects,
      VersandstatusPollingEffects,
      VersandstatusHistoryCacheCleanupEffects,
    ]),
    StoreDevtoolsModule.instrument({name: 'Fakturierung', maxAge: 25, logOnly: !environment.production}),
    StoreRouterConnectingModule.forRoot({}),
    MatTooltipModule,
    NgxMatomoTrackerModule.forRoot({
      mode: MatomoInitializationMode.AUTO_DEFERRED,
      requireConsent: MatomoConsentMode.TRACKING,
    })
  ],
  exports: [
    AuthModule,
  ],
  providers: [
    {
      provide: VISIBILITY_STREAM,
      useFactory: visibilityStreamFactory,
    },
    OidcAuthInterceptor,
    {
      // INFO: Interceptor um automatisch da OAuth Token zu setzen
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    {
      // INFO: Initializer das Logging
      provide: APP_INITIALIZER,
      useFactory: (configService: ConfigService, logger: NGXLogger) => () => {
        return configService.loaded$
          .pipe(
            take(1),
            tap(value => {
              logger.updateConfig({
                ...logger.getConfigSnapshot(),
                level: value.logging.level
              });
            }),
          );
      },
      deps: [ConfigService, NGXLogger],
      multi: true,
    },
    {
      // INFO: Initializer für Sentry Integration
      provide: APP_INITIALIZER,
      useFactory: (
        configService: ConfigService,
        contextInfoService: ContextInfoService,
        logger: NGXLogger,
      ) => () => {
        return configService.loaded$
          .pipe(
            take(1),
            tap(config => {
              const sentry = config.sentry;
              const info = environment.info;
              if (sentry) {
                Sentry.init({
                  enabled: sentry.enabled,
                  debug: sentry.debug,
                  environment: config.environment,
                  release: info.commit,
                  dsn: sentry.dsn,
                  sampleRate: sentry.sampleRate,
                  autoSessionTracking: false,
                  enableTracing: false,
                  // maxBreadcrumbs nur erhöhen, wenn niemals sensiblen Daten mitgeschrieben werden
                  // https://docs.sentry.io/platforms/javascript/guides/angular/configuration/integrations/default/#breadcrumbs
                  maxBreadcrumbs: 0,
                  initialScope: {
                    extra: {
                      'info': info,
                    },
                  },
                  beforeSend: (event) => {
                    let contextInfo = contextInfoService?.contextInfo;
                    if (contextInfo?.properties.get(WellKnownJustFarmingProperties.ERROR_REPORTING)) {
                      return event;
                    }
                    logger.debug('Error reporting is disabled, dropping event');
                    return null;
                  },
                  beforeSendTransaction: (event) => {
                    let contextInfo = contextInfoService?.contextInfo;
                    if (
                      contextInfo?.properties.get(WellKnownJustFarmingProperties.ANALYTICS)
                      && contextInfo?.properties.get(WellKnownJustFarmingProperties.ERROR_REPORTING)
                    ) {
                      return event;
                    }
                    logger.debug('Error reporting or analytics are disabled, dropping transaction event');
                    return null;
                  },
                  beforeBreadcrumb: (breadcrumb) => {
                    let contextInfo = contextInfoService?.contextInfo;
                    if (
                      contextInfo?.properties.get(WellKnownJustFarmingProperties.ANALYTICS)
                      && contextInfo?.properties.get(WellKnownJustFarmingProperties.ERROR_REPORTING)
                    ) {
                      return breadcrumb;
                    }
                    logger.debug('Error reporting or analytics are disabled, dropping breadcrumb');
                    return null;
                  },
                });
              }
            }),
          );
      },
      deps: [
        ConfigService,
        ContextInfoService,
        NGXLogger,
      ],
      multi: true,
    },
    {
      // INFO: Angular Sentry ErrorHandler
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (
        configService: ConfigService,
        matomoInitializerService: MatomoInitializerService,
      ) => () => {
        return configService.loaded$
          .pipe(
            take(1),
            tap(config => {
              const matomoConfig = config.matomo;

              if (
                matomoConfig?.enabled
                && matomoConfig?.trackerUrl
                && matomoConfig?.siteId
              ) {
                matomoInitializerService.initializeTracker({
                  trackerUrl: matomoConfig.trackerUrl,
                  siteId: matomoConfig.siteId,
                });

              }
            }),
          );
      },
      deps: [
        ConfigService,
        MatomoInitializerService,
      ],
      multi: true,
    },
    {
      // INFO: Initializer der die Base-URL setzt
      provide: APP_INITIALIZER,
      useFactory: (
        configService: ConfigService,
        fakturierungConfiguration: FakturierungConfiguration,
      ) => () => {
        return configService.loaded$
          .pipe(
            take(1),
            tap(value => {
              fakturierungConfiguration.basePath = value.fakturierung.apiBaseUrl;
            }),
          );
      },
      deps: [ConfigService, FakturierungConfiguration],
      multi: true,
    },
    {
      // INFO: Initializer für Portal integration die Base-URL setzt
      provide: APP_INITIALIZER,
      useFactory: (
        configService: ConfigService,
        fakturierungConfiguration: FakturierungConfiguration,
        portalMessageBroker: PortalMessageBroker,
        portalTokenService: PortalTokenService,
        oidcSecurityService: OidcSecurityService,
      ) => () => {
        return configService.loaded$
          .pipe(
            take(1),
            mergeMap(config => {
              return fromPromise(portalMessageBroker.init(config.portal.targetOrigin))
                .pipe(map(() => config));
            }),
            mergeMap(config => {
              return fromPromise(portalTokenService.init(config.auth.renewTimeBeforeTokenExpiresInSeconds))
                .pipe(map(() => config));
            }),
            tap(() => {
              const runningEmbedded = portalMessageBroker.isRunningInPortal();
              Sentry.setTag('runningEmbedded', runningEmbedded);

              if (runningEmbedded) {
                const decoded = jwtDecode<JwtPayload>(portalTokenService.getAdnovaToken());
                const token = decoded as any;

                const email = token.email;
                const username = token.preferred_username;
                const name = token.name;

                Sentry.withScope(scope => {
                  let user = scope.getUser();
                  if (!user) {
                    user = {};
                  }
                  user.id = decoded.sub;
                  user.email = email;
                  user.username = username;
                  user['name'] = name;

                  scope.setUser(user);
                });
              } else {
                oidcSecurityService.getAccessToken()
                  .pipe(
                    filter(value => {
                      if (value) {
                        return true;
                      }
                      return false;
                    }),
                  )
                  .subscribe(accessToken => {
                    const decoded = jwtDecode<JwtPayload>(accessToken);
                    const token = decoded as any;

                    const email = token.email;
                    const username = token.preferred_username;
                    const name = token.name;

                    Sentry.withScope(scope => {
                      let user = scope.getUser();
                      if (!user) {
                        user = {};
                      }
                      user.id = decoded.sub;
                      user.email = email;
                      user.username = username;
                      user['name'] = name;

                      scope.setUser(user);
                    });
                  });
              }
            }),
          );
      },
      deps: [ConfigService, FakturierungConfiguration, PortalMessageBroker, PortalTokenService, OidcSecurityService],
      multi: true,
    },
    provideDialogs([
      {
        key: 'kundeDialog',
        isOpenSelector: KundeDialogSelectors.isKundeDialogOpen as MemoizedSelector<object, boolean>,
        closeAction: KundeDialogActions.close,
        loader: () => import('./modules/dialogs/kunde-dialog/kunde-dialog.component').then(c => c.KundeDialogComponent),
        config: {
          width: '575px',
          minWidth: '360px',
          disableClose: true,
          autoFocus: false,
          showCloseButton: false,
          restoreFocus: false,
        },
      },
      {
        key: 'deleteKundeDialog',
        isOpenSelector: DeleteKundeDialogSelectors.isDeleteKundeDialogOpen as MemoizedSelector<object, boolean>,
        closeAction: DeleteKundeDialogActions.close,
        loader: () => import('./modules/dialogs/delete-kunde-dialog/delete-kunde-dialog.component').then(c => c.DeleteKundeDialogComponent),
        config: {
          width: '530px',
          minWidth: '530px',
          disableClose: true,
          autoFocus: false,
          showCloseButton: false,
          restoreFocus: false,
        },
      },
      {
        key: 'produktDialog',
        isOpenSelector: ProduktDialogSelectors.isOpen as MemoizedSelector<object, boolean>,
        closeAction: ProduktDialogActions.close,
        loader: () => import('./modules/dialogs/produkt-dialog/produkt-dialog.component').then(c => c.ProduktDialogComponent),
        config: {
          width: '575px',
          minWidth: '360px',
          disableClose: true,
          autoFocus: false,
          showCloseButton: false,
          restoreFocus: false,
        },
      },
      {
        key: 'deleteProduktDialog',
        isOpenSelector: DeleteProduktDialogSelectors.isDeleteProduktDialogOpen as MemoizedSelector<object, boolean>,
        closeAction: DeleteProduktDialogActions.close,
        loader: () => import('./modules/dialogs/delete-produkt-dialog/delete-produkt-dialog.component').then(c => c.DeleteProduktDialogComponent),
        config: {
          width: '530px',
          minWidth: '530px',
          disableClose: true,
          autoFocus: false,
          showCloseButton: false,
          restoreFocus: false,
        },
      },
      {
        key: 'uploadLogoDialog',
        isOpenSelector: UploadLogoDialogSelectors.isUploadLogoDialogOpen as MemoizedSelector<object, boolean>,
        closeAction: UploadLogoDialogActions.close,
        loader: () => import('./modules/dialogs/upload-logo-dialog/upload-logo-dialog.component').then(c => c.UploadLogoDialogComponent),
        config: {
          width: '575px',
          minWidth: '480px',
          disableClose: true,
          autoFocus: false,
          showCloseButton: true,
          restoreFocus: false,
        },
      },
      {
        key: 'emptyBelegeDialog',
        isOpenSelector: BelegeEmptyStateDialogSelectors.isBelegeEmptyStateDialogOpen as MemoizedSelector<object, boolean>,
        closeAction: BelegeEmptyStateDialogActions.close,
        loader: () => import('./modules/dialogs/empty-belege-dialog/empty-belege-dialog.component').then(c => c.EmptyBelegeDialogComponent),
        config: {
          width: '540px',
          minWidth: '540px',
          disableClose: true,
          autoFocus: false,
          showCloseButton: true,
        },
      },
      {
        key: 'sendenDialog',
        isOpenSelector: SendenDialogSelectors.isSendenDialogOpen as MemoizedSelector<object, boolean>,
        closeAction: SendenDialogActions.close,
        loader: () => import('./modules/dialogs/senden-dialog/senden-dialog.component').then(c => c.SendenDialogComponent),
        config: {
          width: '720px',
          disableClose: true,
          autoFocus: false,
          showCloseButton: false,
          restoreFocus: false,
        },
      },
      {
        key: 'deleteFakturierungsbelegDialog',
        isOpenSelector: DeleteFakturierungsbelegDialogSelectors.isDeleteFakturierungsbelegDialogOpen as MemoizedSelector<object, boolean>,
        closeAction: DeleteFakturierungsbelegDialogActions.close,
        loader: () => import('./modules/dialogs/delete-fakturierungsbeleg-dialog/delete-fakturierungsbeleg-dialog.component').then(c => c.DeleteFakturierungsbelegDialogComponent),
        config: {
          width: '530px',
          minWidth: '530px',
          disableClose: true,
          autoFocus: false,
          showCloseButton: false,
          restoreFocus: false,
        },
      },
      {
        key: 'versandstatusHistoryDialog',
        isOpenSelector: VersandstatusHistoryDialogSelectors.isVersandstatusHistoryDialogOpen as MemoizedSelector<object, boolean>,
        closeAction: VersandstatusHistoryDialogActions.close,
        loader: () => import('./modules/dialogs/versandstatus-history-dialog/versandstatus-history-dialog.component').then(c => c.VersandstatusHistoryDialogComponent),
        config: {
          width: '530px',
          autoFocus: false,
          showCloseButton: true,
          restoreFocus: false,
        },
      },
      {
        key: 'cancelFakturierungsbelegDialog',
        isOpenSelector: CancelFakturierungsbelegDialogSelectors.isCancelFakturierungsbelegDialogOpen as MemoizedSelector<object, boolean>,
        closeAction: CancelFakturierungsbelegDialogActions.close,
        loader: () => import('./modules/dialogs/cancel-fakturierungsbeleg-dialog/cancel-fakturierungsbeleg-dialog.component').then(c => c.CancelFakturierungsbelegDialogComponent),
        config: {
          width: '530px',
          minWidth: '530px',
          disableClose: true,
          autoFocus: false,
          showCloseButton: false,
          restoreFocus: false,
        },
      },
    ]),
    {
      provide: LOCALE_ID,
      useValue: 'de-DE',
    },
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
      useValue: {
        disableTooltipInteractivity: true,
        showDelay: 600,
        hideDelay: 150,
      },
    },
    provideTheme(),
    {
      provide: CONTEXT_INFO,
      useClass: ContextInfoService,
    },
    {
      provide: INHABER_SERVICE,
      useClass: BetriebService,
    },
    {
      provide: NGX_LOGGER,
      useClass: NGXLogger,
    },
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
}
